import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';

interface ProtectedRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
}

/**
 * 受保护的路由组件
 * 检查用户是否已认证，未认证则重定向到登录页面
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  redirectTo = '/auth' 
}) => {
  const authState = useSelector((state: RootState) => (state as any)?.auth ?? {}) as any;
  const isAuthenticated = Boolean(authState?.isAuthenticated);
  const location = useLocation();

  // 如果正在进行身份检查（例如应用初始化时 dispatch getCurrentUser）
  // 不要立刻重定向，等检查完成之后再根据结果决定是否跳转。
  // 这样可以避免页面刷新时的 race condition：路由先渲染导致未认证而被重定向，
  // 然后后台才把 session 重建并把 isAuthenticated 设为 true。
  if (authState?.loading) {
    // 可以回傳 null（不渲染任何東西）或返回一個 loading 元件。
    // loading state: do not render or redirect until auth check finishes
    return null;
  }

  if (!isAuthenticated) {
    try {
      const ref = (location && (location.pathname + (location.search || ''))) || '/';
      return <Navigate to={`${redirectTo}?referer=${encodeURIComponent(ref)}`} replace />;
    } catch (e) {
      return <Navigate to={redirectTo} replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
